<template>
  <div>
    <div
      class="text-center"
      v-if="!dataLoaded"
    >
      <v-progress-circular
        class="mt-6"
        color="primary"
        :size="50"
        indeterminate
      />
    </div>

    <div v-show="dataLoaded">
      <v-row class="my-0">
        <v-col
          cols="12"
          class="pt-0"
        >
          <v-card
            ref="mainCard"
            class="base-card pb-4 pl-4 shadow"
            :class="{
              'pr-4': !isVerticalScrollInTable,
              'pr-0': isVerticalScrollInTable
            }"
          >
            <v-data-table
              ref="table"
              class="base-table fixed-last-column shadow-for-last-column is-vertical-scroll elevation-0 mt-3"
              :class="{
                'shadow-last-column': isShadowForLastColumn
              }"
              :loading="tableLoading"
              :headers="tableHeaders"
              :items="works"
              :items-per-page="-1"
              loading-text="Загрузка"
              no-data-text="Нет данных"
              no-results-text="Не найдено"
              hide-default-footer
              fixed-header
              v-scroll.self="tableScroll"
            >
              <template v-slot:body.prepend="">
                <div
                  v-if="!tableLoading"
                  style="height: 1px"
                ></div>
              </template>

              <template v-slot:item.index="{ index }">
                {{ index + 1 }}
              </template>

              <template v-slot:item.commonWork.authors="{ item }">
                {{ getAuthorsList(item.commonWork.authors.list) }}
              </template>

              <template v-slot:item.actions="{ item }">
                <div class="table-icons">
                  <v-icon
                    class="uikit-icon arrow-top-right-bold-box-outline show-btn"
                    title="Перейти к работе"
                    @click="process(item)"
                  />
                </div>
              </template>

              <template slot="no-data">
                <div>Таблица пуста</div>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import { commonWorkApi } from '@/api'
import { getWorkTypeNameById } from '@/scripts'
import { mapGetters } from 'vuex'
import { CommonWork, Filters } from '@/models'
import { cloneDeep } from 'lodash'
import { shadowForLastColumn } from '@/mixins/shadowForLastColumn'
import { tableHeight } from '@/mixins/tableHeight'
import { isVerticalScrollInTable } from '@/mixins/isVerticalScrollInTable'
import { setExcludeType } from '@/mixins/setExcludeType'

export default {
  name: 'AllWorks',
  mixins: [shadowForLastColumn, tableHeight, isVerticalScrollInTable, setExcludeType],

  metaInfo: {
    title: 'Научные работы'
  },

  props: {
    filtersProp: {
      type: Object
    },
    triggerSetTableHeight: {
      type: Boolean
    }
  },

  created() {
    this.setShowFilterFields()

    this.filters = new Filters(this.savedFilter('review'))
    this.setExcludedTypeForShow()

    this.getCommonWorkParent()
  },

  data: () => ({
    tableHeaders: [
      { text: '№', value: 'index', sortable: false, align: 'start' },
      { text: 'ФИО сотрудника', value: 'commonWork.authors', sortable: false, align: 'start' },
      { text: 'Тип работы', value: 'commonWork.workType.name', sortable: false, align: 'start' },
      { text: 'Год', value: 'commonWork.year.year', sortable: false, align: 'start' },
      { text: 'Наименование работы', value: 'commonWork.workName', sortable: false, align: 'start' },
      { text: 'Наименование издания', value: 'magazine.name', sortable: false, align: 'start' },
      { text: 'Действия', value: 'actions', sortable: false, align: 'end', width: '64px' }
    ],

    works: [],
    tableLoading: false,
    selectedItem: null,
    confirmDialog: false,
    dataLoaded: false,
    pageNumber: 0,
    lastPage: false,
    total: 0,
    filters: new Filters(),
    excludeTypes: [19],
    errorLoad: false
  }),

  computed: {
    ...mapGetters('filters', ['savedFilter']),
    ...mapGetters('user', ['userId'])
  },

  methods: {
    setShowFilterFields() {
      this.$emit('setShowFilterFields', {
        isShowSearchString: false,
        isShowWorkTypeId: true,
        isShowStatusId: false,
        isShowYearId: true,
        isShowDepartmentList: true,
        isShowEmployeeId: true
      })
    },

    async getCommonWorkParent() {
      if (this.errorLoad) return
      try {
        if (!this.lastPage) {
          this.tableLoading = true
          this.filters.setExcludedType(this.excludeTypes)
          const reviewStatus = { id: 2, name: 'Отправлено на рассмотрение' }
          this.filters.statusId = reviewStatus.id
          const { content, last, totalElements } = await commonWorkApi.findAllByFilters(
            this.pageNumber,
            this.filters.getForAPI()
          )
          if (this.pageNumber === 0) this.works = []
          this.lastPage = last
          this.total = totalElements
          this.pageNumber++

          content.length &&
            content.forEach(obj =>
              this.works.push({
                magazine: obj.work.magazine,
                commonWork: new CommonWork(obj.work.commonWork)
              })
            )
          this.checkHeightElementAndChangeIsVerticalScrollInTable()
          this.tableLoading = false
        }
      } catch (e) {
        console.log(e)
        if (e) {
          this.tableLoading = false
          this.errorLoad = true
          this.showErrorMessage(e, 'Не удалось получить список работ')
        } else this.tableLoading = true
      } finally {
        this.dataLoaded = true
      }
    },

    getFullEngName(author) {
      return `${author.lastNameEng || ''} ${author.firstNameEng || ''} ${author.middleNameEng || ''}`
    },

    getAuthorsList(authorList) {
      let authors = ''
      for (const author of authorList)
        authors +=
          (author.employee?.fullName ||
            author.nameRus ||
            this.getFullEngName(author) ||
            'Отсутствует информации об имени') + ', '
      authors = authors.slice(0, -2)
      return authors
    },

    setExcludedTypeForShow() {
      this.setExcludedType()
      this.$emit('setExcludeTypes', this.filters.excludedType)
    },

    process(item) {
      this.$router.push(
        '/works/' + getWorkTypeNameById(item.commonWork.workType.id) + '/process/' + item.commonWork.workId
      )
    },

    remove(item) {
      this.selectedItem = item
      this.confirmDialog = true
    },

    tableScroll(event) {
      const el = event.target
      if (el.scrollHeight - el.clientHeight - el.scrollTop <= 10 && !this.tableLoading) {
        this.getCommonWorkParent()
      }
    }
  },

  watch: {
    dataLoaded() {
      this.setTableHeight()
    },

    triggerSetTableHeight() {
      this.setTableHeight()
    },
    filtersProp: {
      deep: true,
      async handler(val) {
        if (this.dataLoaded) {
          this.filters = cloneDeep(val)
          this.pageNumber = 0
          this.lastPage = false
          this.total = 0
          if (this.userId) {
            await this.getCommonWorkParent()
          }
        }
      }
    }
  }
}
</script>

<style scoped></style>
